import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../global-components/src";
import { useMessage } from "../../../global-components/src/index";
import { READBOOKS_API_URL } from "../../../global-components/src/utils/vars";
import { errorMessage } from "../../../global-components/src/utils/text";
import Spinner from "../../../global-components/src/components/spinner/spiner";
import { DollarSign, Clock, TrendingUp, ArrowDownCircle, AlertCircle } from "react-feather";
import "./style.css";

const fmt = (v: number) =>
    new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);

interface PendingDetail {
    amount: number;
    description: string;
    available_at: string;
}

interface BalanceData {
    available_balance: number;
    pending_balance: number;
    total_earned: number;
    pending_details: PendingDetail[];
}

const PIX_KEY_TYPES = [
    { value: "cpf",    label: "CPF" },
    { value: "email",  label: "E-mail" },
    { value: "phone",  label: "Telefone" },
    { value: "random", label: "Chave aleatória" },
];

export default function AuthorBalancePage() {
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const navigate = useNavigate();

    const [data, setData] = useState<BalanceData | null>(null);
    const [loading, setLoading] = useState(true);

    const [showWithdraw, setShowWithdraw] = useState(false);
    const [pixKey, setPixKey] = useState("");
    const [pixKeyType, setPixKeyType] = useState("cpf");
    const [withdrawAmount, setWithdrawAmount] = useState("");
    const [withdrawLoading, setWithdrawLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string[]>>({});

    const load = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${READBOOKS_API_URL}/author/balance`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setData(res.data);
        } catch (err: any) {
            setMessage(errorMessage(err), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const submitWithdraw = async () => {
        if (withdrawLoading) return;
        setErrors({});
        const amount = parseFloat(withdrawAmount.replace(",", "."));

        if (!pixKey.trim()) { setErrors({ pix_key: ["Informe sua chave PIX"] }); return; }
        if (!amount || amount < 10) { setErrors({ amount: ["Valor mínimo para saque é R$ 10,00"] }); return; }
        if (data && amount > data.available_balance) { setErrors({ amount: ["Saldo insuficiente"] }); return; }

        setWithdrawLoading(true);
        try {
            await axios.post(
                `${READBOOKS_API_URL}/author/withdraw`,
                { amount, pix_key: pixKey, pix_key_type: pixKeyType },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage("Saque solicitado! O PIX chegará em até 1 dia útil.", "success");
            setShowWithdraw(false);
            setPixKey("");
            setWithdrawAmount("");
            load();
        } catch (err: any) {
            if (err?.response?.data?.errors) setErrors(err.response.data.errors);
            else setMessage(errorMessage(err), "error");
        } finally {
            setWithdrawLoading(false);
        }
    };

    const formatDate = (iso: string) => {
        const d = new Date(iso);
        return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });
    };

    if (loading) {
        return <div className="balance-loading"><Spinner size="36px" /></div>;
    }

    return (
        <div className="balance-wrapper margin-top-menu container">
            <div className="balance-header">
                <h1>Meus Ganhos</h1>
                <p>Acompanhe o saldo das suas vendas</p>
            </div>

            {/* Cards de saldo */}
            <div className="balance-cards">
                <div className="balance-card balance-card--available">
                    <div className="balance-card-icon"><DollarSign size={20} /></div>
                    <div>
                        <span className="balance-card-label">Disponível para saque</span>
                        <span className="balance-card-value">{fmt(data?.available_balance ?? 0)}</span>
                    </div>
                </div>

                <div className="balance-card balance-card--pending">
                    <div className="balance-card-icon"><Clock size={20} /></div>
                    <div>
                        <span className="balance-card-label">Aguardando liberação</span>
                        <span className="balance-card-value">{fmt(data?.pending_balance ?? 0)}</span>
                    </div>
                </div>

                <div className="balance-card balance-card--total">
                    <div className="balance-card-icon"><TrendingUp size={20} /></div>
                    <div>
                        <span className="balance-card-label">Total ganho</span>
                        <span className="balance-card-value">{fmt(data?.total_earned ?? 0)}</span>
                    </div>
                </div>
            </div>

            {/* Info: por que o saldo fica bloqueado */}
            <div className="balance-info-box">
                <AlertCircle size={15} />
                <span>
                    O saldo de cada venda fica disponível após <strong>7 dias</strong> (prazo de arrependimento).
                    A plataforma retém <strong>30%</strong> como taxa de serviço.
                </span>
            </div>

            {/* Botão de saque */}
            <div className="balance-actions">
                <button
                    className="btn btn-primary balance-withdraw-btn"
                    disabled={(data?.available_balance ?? 0) < 10}
                    onClick={() => setShowWithdraw(v => !v)}
                >
                    <ArrowDownCircle size={16} />
                    {showWithdraw ? "Cancelar" : "Solicitar saque"}
                </button>
                {(data?.available_balance ?? 0) < 10 && (
                    <span className="balance-min-hint">Mínimo de R$ 10,00 para sacar</span>
                )}
            </div>

            {/* Form de saque */}
            {showWithdraw && (
                <div className="balance-withdraw-form">
                    <h5>Solicitar saque via PIX</h5>

                    <div className="mb-3">
                        <label className="form-label">Tipo de chave PIX</label>
                        <select
                            className="form-select"
                            value={pixKeyType}
                            onChange={e => setPixKeyType(e.target.value)}
                        >
                            {PIX_KEY_TYPES.map(t => (
                                <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                        </select>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Chave PIX</label>
                        <input
                            className={`form-control ${errors.pix_key ? "is-invalid" : ""}`}
                            value={pixKey}
                            onChange={e => setPixKey(e.target.value)}
                            placeholder={
                                pixKeyType === "cpf" ? "000.000.000-00" :
                                pixKeyType === "email" ? "seu@email.com" :
                                pixKeyType === "phone" ? "+55 11 99999-9999" :
                                "Chave aleatória"
                            }
                        />
                        {errors.pix_key && <div className="invalid-feedback">{errors.pix_key[0]}</div>}
                    </div>

                    <div className="mb-3">
                        <label className="form-label">
                            Valor para sacar
                            <span className="balance-available-hint">
                                Disponível: {fmt(data?.available_balance ?? 0)}
                            </span>
                        </label>
                        <div className="input-group">
                            <span className="input-group-text">R$</span>
                            <input
                                type="number"
                                className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                                value={withdrawAmount}
                                onChange={e => setWithdrawAmount(e.target.value)}
                                placeholder="0,00"
                                min="10"
                                step="0.01"
                                max={data?.available_balance}
                            />
                            {errors.amount && <div className="invalid-feedback">{errors.amount[0]}</div>}
                        </div>
                    </div>

                    <button
                        className="btn btn-success w-100"
                        onClick={submitWithdraw}
                        disabled={withdrawLoading}
                    >
                        {withdrawLoading ? <Spinner size="18px" /> : "Confirmar saque"}
                    </button>
                </div>
            )}

            {/* Pendentes em detalhe */}
            {(data?.pending_details?.length ?? 0) > 0 && (
                <div className="balance-pending-section">
                    <h5>Valores em liberação</h5>
                    <div className="balance-pending-list">
                        {data!.pending_details.map((item, i) => (
                            <div key={i} className="balance-pending-item">
                                <div>
                                    <span className="pending-desc">{item.description}</span>
                                    <span className="pending-date">Libera em {formatDate(item.available_at)}</span>
                                </div>
                                <span className="pending-amount">{fmt(item.amount)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}